import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Newspaper, Calendar, User, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { ShareButtons } from "@/components/ShareButtons";
import { Button } from "@/components/ui/button";

export default function NewsDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/content/press/${id}`)
      .then((r) => setItem(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center">
      <Newspaper className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
      <h1 className="font-heading font-black text-2xl">Statement not found</h1>
      <p className="text-muted-foreground mt-2">This news item may have been removed.</p>
      <Link to="/media"><Button className="mt-6 rounded-full bg-primary font-bold gap-2"><ArrowLeft className="w-4 h-4" /> Back to Media & Press</Button></Link>
    </div>
  );

  return (
    <div>
      <section className="topo-bg text-white py-16">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/media" className="inline-flex items-center gap-1.5 text-emerald-100 hover:text-white text-sm mb-6"><ArrowLeft className="w-4 h-4" /> All statements</Link>
          <span className="eyebrow text-secondary">Official Statement</span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading font-black text-3xl md:text-4xl mt-3 leading-tight">{item.title}</motion.h1>
          <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-emerald-50">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4 text-secondary" /> {item.author}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-secondary" /> {(item.date || "").slice(0, 10)}</span>
          </div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 py-12">
        {item.image_path && (
          <img src={`${API}/files/${item.image_path}`} alt={item.title} className="w-full rounded-2xl mb-8 max-h-[420px] object-cover shadow-lg" />
        )}
        <div className="prose prose-slate max-w-none">
          {(item.body || "").split("\n").filter(Boolean).map((para, i) => (
            <p key={i} className="text-lg text-slate-700 leading-relaxed mb-4">{para}</p>
          ))}
        </div>

        <div className="mt-10 border-t border-slate-200 pt-6">
          <p className="text-sm font-semibold text-slate-800 mb-3">Share this statement so more people hear the truth:</p>
          <ShareButtons url={`${API}/news/${id}/share`} title={item.title} testid="news-share" />
        </div>

        <div className="mt-10 rounded-2xl topo-bg text-white p-6 text-center">
          <p className="font-heading font-bold text-lg">Stay informed. Join the movement.</p>
          <Link to="/volunteer"><Button className="mt-4 rounded-full bg-secondary text-slate-900 font-bold hover:bg-white transition-colors">Become a Member</Button></Link>
        </div>
      </article>
    </div>
  );
}
